import type { FC } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useCampaignsConsumption } from "app/pages/digital-marketing/keywords-campaigns/infra/keywords-campaign-hooks/useCampaignsConsumption";
import { useCampaignsState } from "app/pages/digital-marketing/keywords-campaigns/infra/keywords-campaign-hooks/useCampaignsState";
import { useCurrentCampaign } from "app/pages/digital-marketing/keywords-campaigns/infra/keywords-campaign-hooks/useCurrentCampaign";
import { useHomePage } from "app/pages/digital-marketing/keywords-campaigns/infra/keywords-campaign-hooks/useHomePage";
import { useLanguagesAndLocations } from "app/pages/digital-marketing/keywords-campaigns/infra/keywords-campaign-hooks/useLanguagesAndLocations";
import { useLoadingState } from "app/pages/digital-marketing/keywords-campaigns/infra/keywords-campaign-hooks/useLoadingState";
import type {
    IKeywordsCampaignsContext,
    IKeywordsCampaignsProviderProps,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { useDemoCampaignsQuery } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";

const context = createContext<IKeywordsCampaignsContext>(null);

export const useKeywordsCampaignsContext = () => {
    return useContext(context);
};

export const KeywordsCampaignsProvider: FC<IKeywordsCampaignsProviderProps> = ({ children }) => {
    const { params } = useCurrentStateAndParams<RTRawParams>();
    const { campaignId } = params;
    const { state, dispatch, updateCampaignsFromServer } = useCampaignsState();

    const {
        currentCampaignConfiguration,
        loadingState,
        campaignsConfigurations,
        locationsAndLanguagesLists,
        durationObject,
        campaignsConsumptionMetrics,
        isLoadingLanguagesAndLocations,
        myUserConsumptionMetrics,
    } = state;

    const { data: demoCampaigns = [], isLoading: isLoadingDemoCampaigns } = useDemoCampaignsQuery();
    const {
        updateCampaignsConsumptionFromServer,
        updateLocalCampaignsAfterCreation,
        updateLocalCampaignsAfterEdit,
        updateLocalCampaignsAfterDelete,
    } = useCampaignsConsumption(dispatch, campaignsConfigurations, campaignsConsumptionMetrics);

    const isDemo = useMemo(() => {
        return demoCampaigns.some((c) => c.id === campaignId);
    }, [campaignId, demoCampaigns]);

    const { reloadCurrentCampaign, updateLocalCurrentCampaignConfiguration } = useCurrentCampaign(
        state,
        dispatch,
        isDemo,
        isLoadingDemoCampaigns,
    );

    useLanguagesAndLocations(dispatch, locationsAndLanguagesLists);
    useLoadingState(state, dispatch, isLoadingDemoCampaigns, updateCampaignsFromServer);
    useHomePage(dispatch, state);

    const updateCampaignsDataFromServer = useCallback(async () => {
        await updateCampaignsConsumptionFromServer();
        await updateCampaignsFromServer();
    }, [updateCampaignsConsumptionFromServer, updateCampaignsFromServer]);

    const contextValue: IKeywordsCampaignsContext = {
        loadingState,
        campaignsConfigurations,
        currentCampaignConfiguration,
        locationsAndLanguagesLists,
        durationObject,
        campaignsConsumptionMetrics,
        isLoadingLanguagesAndLocations,
        myUserConsumptionMetrics,
        demoCampaigns,
        updateLocalCurrentCampaignConfiguration,
        updateCurrentCampaign: reloadCurrentCampaign,
        updateCampaignsFromServer,
        updateCampaignsConsumptionFromServer,
        updateLocalCampaignsAfterCreation,
        updateLocalCampaignsAfterEdit,
        updateLocalCampaignsAfterDelete,
        updateCampaignsDataFromServer,
    };

    return <context.Provider value={contextValue}>{children}</context.Provider>;
};
